"use client";
import {
  createContext,
  useContext,
  useEffect,
  useReducer,
  useState,
} from "react";
import { ProductType } from "@/common/course/types";
import {
  addDiscount as addDiscountUtil,
  addToCart as addToCartUtil,
  CartData,
  CartItem,
  clearCart as clearCartUtil,
  getDynamicCartData,
  removeFromCart as removeFromCartUtil,
} from "@/utils/cartUtils";
import { fetchCart, addToCart as addToCartBFF } from "@/lib/ecommerce/bff/cart";
import { useCartCookieValidation } from "@/lib/ecommerce/commercetools/client-only/cartCookieValidation";
import newrelic from "@/lib/monitoring/newrelic";

export type CartOperation =
  | { type: "FETCH_CART" }
  | { type: "REFRESH" }
  | { type: "MERGE" }
  | { type: "ADD_TO_CART"; itemIds: string[] }
  | { type: "REMOVE_FROM_CART"; itemId: string }
  | { type: "UPDATE_QUANTITY"; itemId: string; quantity: number }
  | { type: "CLEAR_CART" }
  | { type: "ADD_DISCOUNT"; discountCode: string };

interface CartState {
  cartData: CartData;
  error: string | null;
  operationInProgress: CartOperation | null;
  operationError: string | null;
}

type CartAction =
  | { type: "SET_CART_DATA"; payload: CartData }
  | { type: "SET_ERROR"; payload: string }
  | { type: "OPERATION_START"; payload: CartOperation }
  | { type: "OPERATION_SUCCESS"; payload: CartData }
  | { type: "OPERATION_ERROR"; payload: string }
  | { type: "OPERATION_COMPLETE" };

const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case "SET_CART_DATA":
      return {
        ...state,
        cartData: action.payload,
        error: null,
      };
    case "SET_ERROR":
      return { ...state, error: action.payload };
    case "OPERATION_START":
      return {
        ...state,
        operationInProgress: action.payload,
        operationError: null,
      };
    case "OPERATION_SUCCESS":
      return {
        ...state,
        cartData: action.payload,
        operationInProgress: null,
        operationError: null,
      };
    case "OPERATION_ERROR":
      return {
        ...state,
        operationInProgress: null,
        operationError: action.payload,
      };
    case "OPERATION_COMPLETE":
      return {
        ...state,
        operationInProgress: null,
      };
    default:
      return state;
  }
};

interface CartContextType {
  state: CartState;
  addItem: (item: CartItem) => void;
  removeItem: (itemId: string) => void;
  updateItemQuantity: (itemId: string, quantity: number) => void;
  addDiscountToCart: (discount: any) => void;
  clearCartItems: () => void;
  refreshCart: () => Promise<void>;
  addToCart: (payload: {
    courseRunKeys?: string[];
    productBundleIds?: string[];
  }) => Promise<void>;
  getTotalItems: () => number;
  shouldShowBadge: boolean;
  addedToCart: boolean;
  setAddedToCart: React.Dispatch<React.SetStateAction<boolean>>;
  isAnyOperationInProgress: boolean;
  isLoading: boolean;
  isMerging: boolean;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCartContext = (): CartContextType => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error("useCartContext must be used within a CartProvider");
  }
  return context;
};

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [addedToCart, setAddedToCart] = useState(false);
  const [state, dispatch] = useReducer(cartReducer, {
    cartData: {
      items: [],
      subtotal: 0,
      discounts: [],
      total: 0,
      tax: 0,
      shipping: [],
      enrichedProgramInfo: {},
      bundleInfoMap: {},
    },
    error: null,
    operationInProgress: null,
    operationError: null,
  });

  // Use cart cookie validation hook
  useCartCookieValidation();

  // Single useEffect that handles both initialization and merging
  useEffect(() => {
    const loadCartData = async () => {
      try {
        dispatch({
          type: "OPERATION_START",
          payload: { type: "MERGE" },
        });

        // Single API call that handles both cart fetching AND merging
        const cartData = await fetchCart();

        if (cartData) {
          dispatch({ type: "OPERATION_SUCCESS", payload: cartData });
        } else {
          dispatch({ type: "OPERATION_COMPLETE" });
        }
      } catch (error) {
        newrelic.noticeError(
          error instanceof Error
            ? error
            : new Error("Cart initialization failed"),
          { operation: "initialization" },
        );
        dispatch({
          type: "OPERATION_ERROR",
          payload:
            error instanceof Error
              ? error.message
              : "Cart initialization failed",
        });
      }
    };

    loadCartData();
  }, []);

  // Listen for cart updates
  useEffect(() => {
    const handleCartUpdate = () => {
      try {
        const cartData = getDynamicCartData();
        dispatch({ type: "SET_CART_DATA", payload: cartData });
      } catch (error) {
        dispatch({ type: "SET_ERROR", payload: "Failed to refresh cart data" });
      }
    };

    window.addEventListener("cartUpdated", handleCartUpdate);
    return () => window.removeEventListener("cartUpdated", handleCartUpdate);
  }, []);

  const addItem = (item: CartItem) => {
    try {
      addToCartUtil(item);
      // Cart will be updated via the event listener
    } catch (error) {
      dispatch({ type: "SET_ERROR", payload: "Failed to add item to cart" });
    }
  };

  const removeItem = (itemId: string) => {
    try {
      removeFromCartUtil(itemId);
      // Cart will be updated via the event listener
    } catch (error) {
      dispatch({
        type: "SET_ERROR",
        payload: "Failed to remove item from cart",
      });
    }
  };

  const updateItemQuantity = (itemId: string, quantity: number) => {
    try {
      // For now, we'll remove and re-add with new quantity
      // This could be optimized later
      const item = state.cartData.items.find((item) => item.id === itemId);
      if (item) {
        removeFromCartUtil(itemId);
        addToCartUtil({ ...item, quantity });
      }
    } catch (error) {
      dispatch({
        type: "SET_ERROR",
        payload: "Failed to update item quantity",
      });
    }
  };

  const addDiscountToCart = (discount: any) => {
    try {
      addDiscountUtil(discount);
      // Cart will be updated via the event listener
    } catch (error) {
      dispatch({
        type: "SET_ERROR",
        payload: "Failed to add discount to cart",
      });
    }
  };

  const clearCartItems = () => {
    try {
      clearCartUtil();
      // Cart will be updated via the event listener
    } catch (error) {
      dispatch({ type: "SET_ERROR", payload: "Failed to clear cart" });
    }
  };

  /**
   * Update the Cart Provider with provided CartData
   * @param cartData
   */
  const updateWithCartData = async (cartData: CartData) => {
    if (cartData) {
      dispatch({ type: "SET_CART_DATA", payload: cartData });
    }
  };

  const refreshCart = async () => {
    try {
      dispatch({
        type: "OPERATION_START",
        payload: { type: "REFRESH" },
      });

      const cartData = await fetchCart();

      if (cartData) {
        dispatch({ type: "OPERATION_SUCCESS", payload: cartData });
      } else {
        // Fallback to static data if no BFF cart found
        const fallbackData = getDynamicCartData();
        dispatch({ type: "OPERATION_SUCCESS", payload: fallbackData });
      }
    } catch (error) {
      newrelic.noticeError(
        error instanceof Error ? error : new Error("Failed to refresh cart"),
        { operation: "refreshCart" },
      );

      // Show error
      dispatch({
        type: "OPERATION_ERROR",
        payload:
          error instanceof Error ? error.message : "Failed to refresh cart",
      });

      // Still provide fallback data
      try {
        const fallbackData = getDynamicCartData();
        dispatch({ type: "SET_CART_DATA", payload: fallbackData });
      } catch (fallbackError) {
        newrelic.noticeError(
          fallbackError instanceof Error
            ? fallbackError
            : new Error("Cart fallback failed"),
          { operation: "refreshCart", errorSource: "fallback" },
        );
      }
    }
  };

  const addToCart = async (payload: {
    courseRunKeys?: string[];
    productBundleIds?: string[];
  }) => {
    const itemIds = [
      ...(payload.courseRunKeys ?? []),
      ...(payload.productBundleIds ?? []),
    ];

    const operation: CartOperation = {
      type: "ADD_TO_CART",
      itemIds,
    };

    try {
      dispatch({ type: "OPERATION_START", payload: operation });

      const updatedCart = await addToCartBFF(payload);

      if (updatedCart) {
        const mergedCart = {
          ...updatedCart,
          enrichedProgramInfo: {
            ...state.cartData.enrichedProgramInfo,
            ...updatedCart.enrichedProgramInfo,
          },
          bundleInfoMap: {
            ...state.cartData.bundleInfoMap,
            ...updatedCart.bundleInfoMap,
          },
        };
        dispatch({ type: "OPERATION_SUCCESS", payload: mergedCart });
      } else {
        // BFF returned null - fallback to refresh
        dispatch({ type: "OPERATION_COMPLETE" });
        await refreshCart();
      }
    } catch (error) {
      newrelic.noticeError(
        error instanceof Error ? error : new Error("Failed to add to cart"),
        { operation: "addToCart", itemIds: itemIds.join(",") },
      );
      dispatch({
        type: "OPERATION_ERROR",
        payload:
          error instanceof Error ? error.message : "Failed to add to cart",
      });

      // Attempt to sync cart state after error
      try {
        await refreshCart();
      } catch (refreshError) {
        newrelic.noticeError(
          refreshError instanceof Error
            ? refreshError
            : new Error("Failed to refresh after add error"),
          { operation: "addToCart", errorSource: "recovery" },
        );
      }
    }
  };

  const getTotalItems = () => {
    return (
      state.cartData.items.reduce((sum, item) => {
        // Add each individual course
        if (!item.bundleId) {
          return sum + 1;
        }
        return sum;
      }, 0) +
      // Next add each bundle (program)
      Object.keys(state.cartData.bundleInfoMap).length
    );
  };

  const shouldShowBadge = getTotalItems() > 0;

  // Derived state helpers
  const isAnyOperationInProgress = state.operationInProgress !== null;
  const isLoading =
    state.operationInProgress?.type === "FETCH_CART" ||
    state.operationInProgress?.type === "REFRESH" ||
    state.operationInProgress?.type === "MERGE";
  const isMerging = state.operationInProgress?.type === "MERGE";

  const contextValue: CartContextType = {
    state,
    addItem,
    removeItem,
    updateItemQuantity,
    addDiscountToCart,
    clearCartItems,
    refreshCart,
    addToCart,
    getTotalItems,
    shouldShowBadge,
    addedToCart,
    setAddedToCart,
    isAnyOperationInProgress,
    isLoading,
    isMerging,
  };

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
}
